/**
 * Variant picker visibility for product-recommendation vertical cards.
 * Show the row only when at least one product in the grid has real variants (>1).
 */

/**
 * @param {object | null | undefined} variant
 * @returns {string}
 */
export function getVariantFormValue(variant) {
  if (!variant) return "";
  const raw =
    variant.id ?? variant.admin_graphql_api_id?.split("/")?.pop() ?? "";
  return raw === "" ? "" : String(raw);
}

/**
 * Shopify Ajax uses `available`; Storefront/Admin GraphQL often uses `availableForSale`.
 * @param {object | null | undefined} variant
 * @returns {boolean}
 */
export function isVariantAvailable(variant) {
  if (!variant) return false;
  if (variant.available === false) return false;
  if (variant.availableForSale === false) return false;
  return true;
}

export const OUT_OF_STOCK_LABEL_SUFFIX = " — Out of stock";

/**
 * Append a clear out-of-stock suffix for selects / dropdown rows.
 * @param {string} label
 * @param {object | boolean | null | undefined} variantOrAvailable
 * @returns {string}
 */
export function withOutOfStockLabel(label, variantOrAvailable) {
  const title = String(label || "").trim();
  const available =
    typeof variantOrAvailable === "boolean"
      ? variantOrAvailable
      : isVariantAvailable(variantOrAvailable);
  if (available) return title || "";
  if (!title) return "Out of stock";
  if (/out of stock/i.test(title)) return title;
  return `${title}${OUT_OF_STOCK_LABEL_SUFFIX}`;
}

/**
 * @param {object[] | null | undefined} variants
 * @param {string | number | null | undefined} formValue
 * @returns {object | null}
 */
export function findVariantByFormValue(variants, formValue) {
  if (!Array.isArray(variants) || variants.length === 0) return null;
  if (formValue == null || formValue === "") return null;
  const target = String(formValue);
  return variants.find((variant) => getVariantFormValue(variant) === target) || null;
}

/**
 * @param {object | null | undefined} product
 * @returns {object[]}
 */
export function getProductVariants(product) {
  if (Array.isArray(product?.variants) && product.variants.length) {
    return product.variants;
  }
  if (
    Array.isArray(product?.productInfo?.variants) &&
    product.productInfo.variants.length
  ) {
    return product.productInfo.variants;
  }
  return [];
}

/**
 * @param {object | null | undefined} product
 * @returns {boolean}
 */
export function productHasSelectableVariants(product) {
  return getProductVariants(product).length > 1;
}

/**
 * @param {object[] | null | undefined} products
 * @returns {boolean}
 */
export function anyProductHasSelectableVariants(products) {
  if (!Array.isArray(products) || products.length === 0) return false;
  return products.some(productHasSelectableVariants);
}
